import React, {useContext, useEffect, useState, useCallback, useMemo} from 'react';
import {Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle} from "react-bootstrap";
import CustomButton from "../../../elements/CustomButton/CustomButton";
import DateTime from 'react-datetime'
import {ApiContext} from "../../../api/Api";
import {TideApiContext} from "../../../api/tideApiConfig";
import useInput from '../../../hooks/useInput';
import _ from 'lodash';
import EditableStockMovementsTable from '../components/EditableStockMovementsTable';
import {NotifierContext} from "../../../utils/Notifier";
import {stockMovementsArrayToBatchMovement} from "../../../utils/modelUtils/almazen/warehouseProductsUtility";
import EntitySelect from "./components/Dev002ProductBrandSelect";
import {stockMovementBatchStatus} from "../../../utils/modelUtils/almazen/stockMovementBatchUtils";
import {convertFromSmallestUnit} from "../../../utils/modelUtils/almazen/unitsUtility";
import {
    getPreferredProductBrandUnit
} from "../../../utils/modelUtils/almazen/productBrandUnitsUtility";
import {
    productBrandForRequisitionSGroups
} from "../../../utils/modelUtils/almazen/productBrandProviderUtils";
import {stockRequestStatus} from "../../../utils/modelUtils/almazen/stockRequestUtils";
import {useSelector} from "react-redux";
import moment from 'moment';
import StockRequestGenerator from "./components/StockRequestGenerator";

const loadingId = '@NewProductRequisitionModal.stockRequests.create';
const selectLabelCreator=(pb)=>`${pb.product.name} - ${pb.brand.name}`;
const additionalFilters={
    'order[product.name]':'ASC',
    'product.isActive':true,
    'productBrandUnits.isActive':true,
    sGroups:productBrandForRequisitionSGroups
};

const EMPTY_PRODUCT_BRANDS = [];

const ProductRequisitionFormModal = ({onHide, onEdit, stockRequest, cloning} )=>{

    const api = useContext( ApiContext);
    const tideApi = useContext(TideApiContext);
    const notifier = useContext( NotifierContext );
    const warehouses=useSelector(({api})=>api.warehouses||[]);
    const places=useSelector(({api})=>api.places||[]);
    const loadingIds=useSelector(({loadingIds})=>loadingIds);
    const [inventories, setInventories] = useState( [] );
    const [fetchedProductBrands, setFetchedProductBrands] = useState( [] );
    const [overFlowingProductBrand, setOverflowingProductBrand] = useState( [] );
    const [showInactiveProducts, setShowInactiveProducts] = useState(false);
    const [inactiveProductBrandIds, setInactiveProductBrandIds] = useState([]);
    const [validatedProductBrandIds, setValidatedProductBrandIds] = useState([]);
    const [validatedWarehouseId, setValidatedWarehouseId] = useState(null);

    useEffect( ()=>{
        api.warehouses.get({pagination:false});
        api.places.get({isActive: true});

    }, [api] );

    const commentIn = useInput('');
    const [requestedForDate, setRequestedForDate]=useState(moment());
    const [stockMovements, setStockMovements] = useState([]);
    const clearStockMovements = useCallback(()=>setStockMovements([]), []);
    const toIn = useInput('');
    const fromIn = useInput('', clearStockMovements);

    const [loadingExistingUnits, setLoadingExistingUnits] = useState(!!stockRequest);

    useEffect(() => {
        if (!stockRequest) return;

        let canceled = false;

        const movements = stockRequest.stockMovementBatches
            .filter(batch => batch.inOrOut !== 'in')
            .flatMap(batch => batch.movements);

        const ids = [...new Set(movements.map(m => m.productBrand.id))];

        setLoadingExistingUnits(true);
        setStockMovements([]);

        fromIn.setValue(stockRequest.fromWarehouse.id);
        toIn.setValue(stockRequest.toPlace.id);

        if (!ids.length) {
            setLoadingExistingUnits(false);
            return () => { canceled = true; };
        }

        api.productBrands.get({
            pageSize: ids.length,
            customProp: 'DEV002ExistingRequisitionProductBrands',
            filters: {id: ids}
        }).then(productBrands => {
            if (canceled) return;

            const byId = new Map(productBrands.map(pb => [pb.id, pb]));

            const restored = movements.map(movement => {
                const productBrand = byId.get(movement.productBrand.id);

                if (!productBrand)
                    throw new Error('No se encontró la marca del producto.');

                const selectedUnit = getPreferredProductBrandUnit(productBrand);

                if (!selectedUnit)
                    throw new Error('El producto no tiene unidades activas configuradas.');

                return {
                    productBrand,
                    selectedUnit,
                    quantity: Math.abs(convertFromSmallestUnit(
                        movement.quantity,
                        selectedUnit,
                        productBrand.product
                    ))
                };
            });

            setStockMovements(restored);
            setLoadingExistingUnits(false);
        }).catch(() => {
            if (canceled) return;
            setStockMovements([]);
            setLoadingExistingUnits(true);
            notifier.error(
                'No fue posible recuperar las unidades autorizadas. ' +
                'No se permite modificar esta requisición.'
            );
        });

        return () => { canceled = true; };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stockRequest, api]);

    useEffect(()=>{
        setOverflowingProductBrand([]);
        setFetchedProductBrands([]);
    },[toIn.value]);

    useEffect(() => {
        let ids = [];
        stockMovements.forEach((sm) => {
            if (!_.includes(fetchedProductBrands, sm.productBrand.id))
                ids.push(sm.productBrand.id);
        });
        if (ids.length > 0) {
            tideApi.inventories.get({ params: { productBrand: ids, warehouse:toIn.value  }}).then((response) => {
                setInventories([...inventories, ...response]);
                setFetchedProductBrands([...fetchedProductBrands, ...ids]);
            });
        }

    }, [stockMovements, tideApi, fetchedProductBrands, inventories, toIn.value]);


    const onProductBrandAdd = useCallback((productBrand) => {
        if (!productBrand) return;

        const isValidated =
            validatedWarehouseId === fromIn.value &&
            validatedProductBrandIds.includes(productBrand.id);

        if (!isValidated) {
            notifier.error('Espera a que se valide la vigencia del producto en este almacén.');
            return;
        }

        if (inactiveProductBrandIds.includes(productBrand.id)) {
            notifier.error('Este producto está inactivo en el almacén seleccionado.');
            return;
        }

        if (_.find(stockMovements, sm => sm.productBrand.id === productBrand.id))
            return;

        const stockMovement = {
            productBrand,
            quantity: '1',
            selectedUnit: getPreferredProductBrandUnit(productBrand)
        };

        setStockMovements([...stockMovements, stockMovement]);
    }, [
        stockMovements,
        validatedWarehouseId,
        validatedProductBrandIds,
        inactiveProductBrandIds,
        fromIn.value,
        notifier
    ]);

    const fromWarehouseId = fromIn.value;
    const productBrandResultsKey = useMemo(
        () => `DEV002RequisitionProductBrands_${fromWarehouseId}_${showInactiveProducts}`,
        [fromWarehouseId, showInactiveProducts]
    );

    useEffect(() => {
        api.clearProperty(productBrandResultsKey);
    }, [api, productBrandResultsKey]);

    const selectorProductBrands = useSelector(
        ({api: apiState}) => apiState[productBrandResultsKey] || EMPTY_PRODUCT_BRANDS
    );

    useEffect(() => {
        let canceled = false;

        setInactiveProductBrandIds([]);
        setValidatedProductBrandIds([]);
        setValidatedWarehouseId(null);

        if (!fromWarehouseId || !selectorProductBrands.length) {
            return () => { canceled = true; };
        }

        const ids = selectorProductBrands.map(productBrand => productBrand.id);

        api.productBrands.get({
            pageSize: 100,
            customProp: 'DEV002InactiveProductBrands',
            filters: {
                id: ids,
                inventoryWarehouse: fromWarehouseId,
                inventoryEnabled: 'false'
            }
        }).then(inactiveProducts => {
            if (canceled) return;

            setInactiveProductBrandIds(inactiveProducts.map(productBrand => productBrand.id));
            setValidatedProductBrandIds(ids);
            setValidatedWarehouseId(fromWarehouseId);
        }).catch(() => {
            if (canceled) return;

            setInactiveProductBrandIds([]);
            setValidatedProductBrandIds([]);
            setValidatedWarehouseId(null);
        });

        return () => { canceled = true; };
    }, [api, fromWarehouseId, selectorProductBrands]);

    const createRequisition = useCallback(() =>{

        if (loadingExistingUnits) {
            return notifier.error(
                'Las unidades de la requisición todavía no están disponibles. ' +
                'No se permite guardar.'
            );
        }

        if( !fromIn.value )
            return notifier.error( 'Debes escoger el almacén de salida del producto.' );

        if( !toIn.value )
            return notifier.error( 'Debes escoger el lugar  de entrega.' );

        if( !stockMovements.length )
            return notifier.error( 'Debes escoger al menos un producto.' );

        // DEV-002: Validate every product before modifying an existing requisition.
        const productBrandIds = [...new Set(
            stockMovements.map(sm => sm.productBrand.id)
        )];

        if (productBrandIds.length > 500) {
            return notifier.error(
                'La requisición supera el límite de 500 insumos para validación.'
            );
        }

        const sourceWarehouseId = fromIn.value;

        return api.productBrands.get({
            pageSize: productBrandIds.length,
            customProp: 'DEV002RequisitionPreflight',
            filters: {
                id: productBrandIds,
                inventoryWarehouse: sourceWarehouseId,
                inventoryEnabled: 'true'
            }
        }).then(enabledProducts => {
            const enabledIds = new Set(enabledProducts.map(product => product.id));
            const unavailableProducts = stockMovements.filter(
                sm => !enabledIds.has(sm.productBrand.id)
            );

            if (unavailableProducts.length) {
                notifier.error(
                    'No se puede guardar: uno o más insumos están deshabilitados ' +
                    'o no existen en el almacén de salida.'
                );
                return;
            }

            const outBatchMovement = stockMovementsArrayToBatchMovement(
                stockMovements,
                sourceWarehouseId,
                'out',
                stockMovementBatchStatus.PENDING_APPROVAL
            );

            const newStockRequest = {
                fromWarehouse: sourceWarehouseId,
                toPlace: toIn.value,
                requestedForDate: requestedForDate.format('YYYY-MM-DDTHH:mm'),
                stockMovementBatches: [outBatchMovement],
                autogeneratedIncomeMovements: true,
                cassette: {
                    comments: commentIn.value ? [{text: commentIn.value}] : []
                }
            };

            const apiCall = () =>
                api.stockRequests.create(newStockRequest, loadingId)
                    .then(stockRequest ? onEdit : onHide);

            if (stockRequest && !cloning) {
                return api.stockRequests.edit({
                    id: stockRequest.id,
                    stockRequest: {status: stockRequestStatus.CANCELED}
                }).then(apiCall).catch(() => {
                    notifier.error(
                        'Falló la actualización de la requisición. ' +
                        'Verifica el estado de la original antes de reintentar.'
                    );
                });
            }

            return apiCall().catch(() => {
                notifier.error(
                    'No fue posible crear la requisición. Verifica si se registró antes de reintentar.'
                );
            });
        }, () => {
            notifier.error(
                'No fue posible validar los insumos. No se inició la modificación.'
            );
        });

    },[ api, notifier, fromIn.value, toIn.value, commentIn.value, stockMovements, onHide, stockRequest, onEdit, requestedForDate, cloning, loadingExistingUnits ]);

    const toWarehouse = toIn.value && _.find(places, p=>p.id===toIn.value);

    const productBrandFilters = useMemo(() => ({
        ...additionalFilters,
        inventoryWarehouse: fromIn.value,
        inventoryEnabled: showInactiveProducts ? undefined : 'true'
    }), [fromIn.value, showInactiveProducts]);


    return (
        <Modal
            show={true}
            bsSize='lg'
            className='ProductRequisitionFormModal'
        >
            <ModalHeader closeButton onHide={onHide}><ModalTitle>Nueva requisición de almacén</ModalTitle></ModalHeader>

            <ModalBody>

                {stockRequest && !cloning &&
                    <div className='alert alert-danger'>Al editar la requisición se cancelará la actual y se generará una nueva con los nuevos productos.</div>
                }
                <h5>Solicitar al almacén:</h5>
                <select className='form-control' {...fromIn.bind}>
                    <option value='' disabled>Selecciona un almacén</option>
                    { warehouses.map( ( wh )=>
                        <option key={wh.id} value={wh.id}>{wh.name}</option>
                    ) }
                </select>

                <h5>Lugar de entrega:</h5>
                <select className='form-control' {...toIn.bind}>
                    <option value='' disabled>Selecciona un lugar</option>
                    { places.map( ( pl )=>
                        fromIn.value===pl.id || !pl.canReceiveProducts? null:
                        <option key={pl.id} value={pl.id}>{pl.name}</option>
                    ) }
                </select>

                <h5>Fecha de entrega:</h5>
                <DateTime
                    viewMode={'time'}
                    value={requestedForDate}
                    onChange={setRequestedForDate}
                />

                <StockRequestGenerator
                    warehouseId={ toWarehouse?.type==='warehouse'&& toWarehouse.id }
                    setStockMovements={setStockMovements}
                />

                {/* Only show the product selector when there's a warehouse selected */}
                {!!fromIn.value &&
                <>
                    <h5>Selecciona los productos que requieres:</h5>
                    {overFlowingProductBrand.length > 0 &&
                        <div className='bg-danger text-center'>
                            <p>Estás rebasando la capacidad máxima que tiene el almacén de entrega en los siguientes artículos:</p>
                            <div className='w-100 d-flex flex-column'>
                                {overFlowingProductBrand.filter((inv) => inv.warehouse.id === toIn.value).map((inv) => (
                                    <p key={inv.productBrand.product.id} className='text-danger'>-- {inv.productBrand.product.name} --</p>
                                ))}
                                <br/>
                            </div>
                        </div>
                    }
                    <br/>

                    {!!stockMovements.length &&
                    <EditableStockMovementsTable
                        inventories={inventories}
                        overFlowingProductBrand={overFlowingProductBrand}
                        setOverflowing={setOverflowingProductBrand}
                        stockMovements={stockMovements}
                        onChange={ setStockMovements }
                    />}

                    <label className='d-flex align-items-center' style={{gap: '8px', marginBottom: '10px'}}>
                        <input
                            type='checkbox'
                            checked={showInactiveProducts}
                            onChange={(event) => setShowInactiveProducts(event.target.checked)}
                        />
                        Mostrar inactivos
                    </label>
                    <EntitySelect
                        key={`${fromWarehouseId}-${showInactiveProducts}`}
                        entity={'productBrands'}
                        apiCustomProp={productBrandResultsKey}
                        onChange={ onProductBrandAdd }
                        filterBy={'search'}
                        labelCreator={selectLabelCreator}
                        optionRenderer={(option) => {
                            const productBrand = option.value;
                            const isInactive = inactiveProductBrandIds.includes(productBrand.id);
                            const isValidated =
                                validatedWarehouseId === fromWarehouseId &&
                                validatedProductBrandIds.includes(productBrand.id);

                            return (
                                <span style={{
                                    color: isInactive ? '#a94442' : undefined,
                                    opacity: !isValidated ? 0.6 : 1
                                }}>
                                    {option.label}
                                    {isInactive
                                        ? ' — INACTIVO (No seleccionable)'
                                        : !isValidated
                                            ? ' — Validando vigencia...'
                                            : ''}
                                </span>
                            );
                        }}
                        maxResults={100}
                        additionalFilters={productBrandFilters}
                    />
                </>}

                <h5>Comentarios:</h5>
                <textarea  className='form-control' {...commentIn.bind} placeholder='¿Quieres agregar algún detalle a tu requisición?' />

            </ModalBody>

            <ModalFooter>
                <CustomButton bsStyle='danger' onClick={onHide} className='pull-left' disabled={!!loadingIds[loadingId]} >Cancelar</CustomButton>
                <CustomButton bsStyle='success' disabled={!!loadingIds[loadingId] || !!overFlowingProductBrand.length} onClick={ createRequisition } >Crear</CustomButton>
            </ModalFooter>

        </Modal>
    )

};

ProductRequisitionFormModal.displayName="ProductRequisitionFormModal";

export default ProductRequisitionFormModal;
